// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { Rectangle } from '@shared/commons/Geometry';
import { BOTTOM_WINDOWS_MARGIN } from '../UtilsClasses/SizeConstants';
import Ractive from 'ractive';
import $ from 'jquery';
import { WDSettings } from '../settings/WDGeneralSettings';
import { ControlsUtils } from '../UtilsClasses/ControlsUtils';
class _RactiveTooltip extends Ractive {
    constructor() {
        super(...arguments);
        this.TOOLTIP_ARROW_OFFSET_X = 6;
        this.TOOLTIP_ARROW_WIDTH = 14;
        this.TOOLTIP_ARROW_HEIGHT = 6;
        this.X_OFFSET = 5;
        this.Y_OFFSET = 20; // pointer height
    }
    getTooltip() {
        if (isNullOrUndefined(this.tooltip)) {
            this.tooltip = $('#tooltipContainer div');
        }
        return this.tooltip;
    }
    showTooltip(text, rect) {
        if (this.lastTooltipText === text &&
            (this.lastRect === rect || (!isNullOrUndefined(this.lastRect) && (rect === null || rect === void 0 ? void 0 : rect.Equals(this.lastRect))))) {
            return;
        }
        if (!isNullOrUndefined(this.tooltipTimeout)) {
            clearTimeout(this.tooltipTimeout);
            this.tooltipTimeout = null;
        }
        if (!isValidString(text) || (isValidString(this.lastTooltipText) && this.lastTooltipText !== text)) {
            hideTooltip();
            return;
        }
        this.tooltipTimeout = setTimeout(() => {
            this.lastTooltipText = text;
            if (!isNullOrUndefined(rect)) {
                this.displayTooltipRelativelyRect(text, rect);
                this.lastRect = rect;
            }
            else {
                this.displayTooltip(text);
                this.lastRect = null;
            }
        }, 500);
    }
    showTooltipForElement(element, text) {
        const elementRect = element.getBoundingClientRect();
        const rect = new Rectangle(elementRect.left, elementRect.top, elementRect.width, elementRect.height);
        this.displayTooltipRelativelyRect(text, rect);
    }
    displayTooltipRelativelyRect(text, rect) {
        let isUpper = true;
        text = Resources.getResource(text);
        text = text.replace('\n', '<br>');
        const tooltip = this.getTooltip();
        tooltip.html(text);
        const windowWidth = global.windowWidth();
        const windowHeight = global.windowHeight();
        const elementOffsetX = Math.round(rect.Width / 2 - (this.TOOLTIP_ARROW_OFFSET_X + this.TOOLTIP_ARROW_WIDTH / 2));
        const tooltipRect = new Rectangle(rect.X + elementOffsetX, rect.Bottom() + this.TOOLTIP_ARROW_HEIGHT, tooltip.outerWidth(), tooltip.outerHeight());
        if (tooltipRect.Bottom() > windowHeight) {
            tooltipRect.Y = rect.Y - tooltipRect.Height - this.TOOLTIP_ARROW_HEIGHT;
            isUpper = false;
        }
        let arrowLeftOffset = 0;
        if (tooltipRect.Right() > windowWidth) {
            arrowLeftOffset = tooltipRect.Right() - windowWidth;
            tooltipRect.X = windowWidth - tooltipRect.Width;
        }
        tooltip.addClass(isUpper ? 'ractive-tooltip-upper' : 'ractive-tooltip-lower');
        tooltip.css({
            left: tooltipRect.X,
            top: tooltipRect.Y,
            '--arrow-left': `${arrowLeftOffset}px`
        });
        this.setTooltipVisibility(true);
    }
    displayTooltip(text) {
        text = Resources.getResource(text);
        text = text.replace('\n', '<br>');
        const tooltip = this.getTooltip();
        tooltip.html(text);
        const w = tooltip.outerWidth();
        const h = tooltip.outerHeight();
        const newCoord = ControlsUtils.CorrectPopupLocation(new Rectangle(this.lastMouseMoveX - RactiveTooltip.X_OFFSET, this.lastMouseMoveY + RactiveTooltip.Y_OFFSET, w, h), null);
        if (window.innerHeight - RactiveTooltip.Y_OFFSET > h + newCoord.newY + BOTTOM_WINDOWS_MARGIN) {
            tooltip.addClass('ractive-tooltip-upper');
        }
        else {
            tooltip.addClass('ractive-tooltip-lower');
            newCoord.newY = this.lastMouseMoveY - h - RactiveTooltip.Y_OFFSET;
        }
        tooltip.css({
            left: newCoord.newX,
            top: newCoord.newY
        });
        this.setTooltipVisibility(true);
    }
    setTooltipVisibility(visible) {
        const tooltip = this.getTooltip();
        tooltip.css('display', visible ? 'block' : 'none');
        if (!visible) {
            tooltip.removeClass('ractive-tooltip-upper');
            tooltip.removeClass('ractive-tooltip-lower');
        }
    }
    onDocumentMouseMove(event) {
        RactiveTooltip.lastMouseMoveX = event.clientX;
        RactiveTooltip.lastMouseMoveY = event.clientY;
    }
    decorator(node, text) {
        const $node = $(node);
        let timeout = null;
        const checkEnability = () => {
            const getRes = this.get();
            const enabled = getRes.enabled;
            const showDisabledToolTip = getRes.showDisabledToolTip;
            return enabled || showDisabledToolTip;
        };
        const mouseover = function (event) {
            if (!checkEnability() || event.currentTarget !== this || !isValidString(text) || !WDSettings.tooltips) {
                return;
            }
            timeout = setTimeout(function (text) {
                showTooltip(node, text);
            }, 500, text);
        };
        const mouseout = function () {
            if (!isNullOrUndefined(timeout)) {
                clearTimeout(timeout);
            }
            hideTooltip();
        };
        $node.on('mouseenter', mouseover);
        $node.on('mouseleave', mouseout);
        $node.on('click', mouseout);
        return {
            teardown: function () {
                $node.off('mouseenter', mouseover);
                $node.off('mouseleave', mouseout);
            }
        };
    }
    hideTooltip() {
        this.lastTooltipText = null;
        this.setTooltipVisibility(false);
        if (!isNullOrUndefined(this.tooltipTimeout)) {
            clearTimeout(this.tooltipTimeout);
            this.tooltipTimeout = null;
        }
    }
    tooltipHandlerInitialize() {
        global.showTooltip = (element, text) => {
            if (!isNullOrUndefined(element)) {
                RactiveTooltip.showTooltipForElement(element, text);
            }
            else {
                RactiveTooltip.showTooltip(text);
            }
        };
        global.hideTooltip = () => {
            RactiveTooltip.hideTooltip();
        };
        global.showTooltipOnCanvas = (text, canvasHtml, rect) => {
            if (!isNullOrUndefined(rect) && !isNullOrUndefined(canvasHtml)) {
                const canvasHTMLRect = canvasHtml.getBoundingClientRect();
                const width = rect.Left() > canvasHTMLRect.width ? canvasHTMLRect.width - 1 : rect.Left();
                const adjustedRect = new Rectangle(canvasHTMLRect.left + rect.X, canvasHTMLRect.top + rect.Y, rect.Width, rect.Height);
                const canvasRect = new Rectangle(canvasHTMLRect.left, canvasHTMLRect.top, canvasHTMLRect.width, canvasHTMLRect.height);
                RactiveTooltip.showTooltip(text, adjustedRect.intersect(canvasRect));
            }
            else {
                RactiveTooltip.showTooltip(text);
            }
        };
    }
}
export const RactiveTooltip = new _RactiveTooltip();
document.addEventListener('mousemove', RactiveTooltip.onDocumentMouseMove);
Ractive.decorators.tooltip = RactiveTooltip.decorator;
